import { Suspense, lazy } from 'react';
import type { ReactNode } from 'react';
import type { RouteObject } from 'react-router-dom';
import { createBrowserRouter } from 'react-router-dom';
import { SkeletonText } from '@argink/ui';
import { RequireAuth } from './auth/RequireAuth';
import { TenantShell } from './components/layout/TenantShell';
import { HomePage } from './pages/HomePage';
import { LoginPage } from './pages/LoginPage';
import { NotFoundPage } from './pages/NotFoundPage';

/**
 * As telas do tenant entram por `lazy()` (resolução T1 #6 do contrato).
 *
 * A razão é performance, não segurança — a fronteira real é a sessão
 * server-side, e bundle nunca foi segredo. O ponto é que, desde M3, `/` é uma
 * Home PÚBLICA: sem isto, todo visitante anônimo baixaria o wizard de envio, o
 * detalhe de documento e o relatório de evidências para ler uma página de
 * apresentação.
 *
 * Home e login ficam no bundle inicial de propósito: são o caminho de entrada, e
 * carregá-los sob demanda trocaria bytes por um atraso na primeira tela.
 */
const DashboardPage = lazy(() =>
  import('./pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const EnvelopesPage = lazy(() =>
  import('./pages/EnvelopesPage').then((m) => ({ default: m.EnvelopesPage })),
);
const EnvelopeNewPage = lazy(() =>
  import('./pages/EnvelopeNewPage').then((m) => ({ default: m.EnvelopeNewPage })),
);
const EnvelopeDetailPage = lazy(() =>
  import('./pages/EnvelopeDetailPage').then((m) => ({ default: m.EnvelopeDetailPage })),
);
const EnvelopeResultPage = lazy(() =>
  import('./pages/EnvelopeResultPage').then((m) => ({ default: m.EnvelopeResultPage })),
);
const EnvelopeEvidencePage = lazy(() =>
  import('./pages/EnvelopeEvidencePage').then((m) => ({ default: m.EnvelopeEvidencePage })),
);
const SettingsPage = lazy(() =>
  import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);

/**
 * Fronteira de Suspense POR ROTA, e não uma só em volta do router inteiro: assim
 * o carregamento do pedaço do tenant nunca desmonta a casca já pintada, e o
 * harness de teste (`useRoutes`) não precisa de tratamento especial.
 */
function Tenant({ children }: { children: ReactNode }) {
  return (
    <RequireAuth>
      <Suspense fallback={<TenantFallback />}>{children}</Suspense>
    </RequireAuth>
  );
}

/**
 * Casca real + skeleton contextual (R-UX02-P01/D-101, §"LOADING DE ROTA" do
 * contrato) — **não** o antigo `<p>Carregando…</p>` solto na página em branco.
 *
 * `TenantShell` monta marca, navegação, tema e topbar de verdade: quem chega
 * aqui enquanto o CHUNK da página (não o dado — isso cada página resolve com
 * seu próprio `useQuery`) ainda baixa continua vendo o produto, não uma tela
 * vazia piscando entre o nada e o conteúdo. `role="status"`/`aria-busy` no
 * corpo é o que avisa leitor de tela — a casca ao redor não é o que está
 * carregando, é o que já está pronto.
 *
 * **Não promove o shell a layout persistente.** Continua um `<Suspense>` POR
 * ROTA (ver `Tenant` abaixo): cada página monta seu PRÓPRIO `TenantShell` de
 * novo quando o chunk chega — isto aqui é só o que se vê ANTES disso, não um
 * `<Outlet/>` que sobrevive à navegação (essa promoção é decisão de lane
 * futura, fora do escopo desta).
 */
function TenantFallback() {
  return (
    <TenantShell title="Carregando…">
      <div className="route-fallback" role="status" aria-busy="true">
        <span className="argink-sr-only">Carregando o conteúdo desta página</span>
        <SkeletonText lines={4} label="" />
      </div>
    </TenantShell>
  );
}

/** As rotas do mandato (§3), sem reload quebrado (React Router 6, AMVP-01).
 * Exportado como array de dados (`routeObjects`) separado do router de browser
 * para `test/rtl/**` poder montar um `createMemoryRouter` com as MESMAS rotas em
 * vez de reimplementar a árvore de navegação no teste. */
export const routeObjects: RouteObject[] = [
  /* `/` era um `Navigate` para `/dashboard` — ou seja, quem chegava ao ARGINK sem
   * sessão caía direto na tela de login, e o produto não tinha porta de entrada.
   * Desde M3 a raiz é a Home pública (§4 do contrato). */
  { path: '/', element: <HomePage /> },
  { path: '/login', element: <LoginPage /> },
  {
    path: '/dashboard',
    element: (
      <Tenant>
        <DashboardPage />
      </Tenant>
    ),
  },
  /* `/envelopes` nasceu em M4: `/dashboard` era a lista, e virou a Visão geral.
   * O prefixo continua o do routing real — `/processos` seria uma segunda família
   * de URL para o mesmo objeto (§24). */
  {
    path: '/envelopes',
    element: (
      <Tenant>
        <EnvelopesPage />
      </Tenant>
    ),
  },
  {
    path: '/envelopes/new',
    element: (
      <Tenant>
        <EnvelopeNewPage />
      </Tenant>
    ),
  },
  {
    path: '/envelopes/:id',
    element: (
      <Tenant>
        <EnvelopeDetailPage />
      </Tenant>
    ),
  },
  {
    path: '/envelopes/:id/result',
    element: (
      <Tenant>
        <EnvelopeResultPage />
      </Tenant>
    ),
  },
  {
    path: '/envelopes/:id/evidence',
    element: (
      <Tenant>
        <EnvelopeEvidencePage />
      </Tenant>
    ),
  },
  {
    path: '/settings',
    element: (
      <Tenant>
        <SettingsPage />
      </Tenant>
    ),
  },
  { path: '*', element: <NotFoundPage /> },
];

export const router = createBrowserRouter(routeObjects);
